import { environment } from '../../../../../environments/environment';

/**
 * Where the CRM, a business login and an applicant all fetch an uploaded permit
 * document from, and whether it can be shown on the page.
 *
 * The permit payload carries `path` (the object's direct S3 URL), `key` (its name in
 * the bucket) and `type` (what was uploaded). Linking `path` only works while the
 * bucket is world-readable, which is not where these documents belong - they are
 * police checks, Ochre cards and shooter's licences. `/api/upload/open/{key}` streams
 * the object through the backend instead, so the same link keeps working once the
 * bucket is closed.
 */
export interface UploadedDocument {
    filename?: string | null;
    key?: string | null;
    type?: string | null;
    path?: string | null;
}

export function uploadedDocumentUrl(doc?: UploadedDocument | null): string | null {
    if (!doc) return null;

    // The object's own URL, which works because the bucket is currently public.
    if (doc.path) return doc.path;

    // /api/upload/open streams the object through the backend, which is where this
    // should end up once the bucket is closed - but not yet, and not like this. The
    // ^/api firewall has entry_point: jwt, so an unauthenticated request is rejected
    // with a 401 regardless of the PUBLIC_ACCESS rule on ^/api/upload, and a browser
    // fetching an <img src> or following an <a href> sends no Authorization header -
    // ngxh6 attaches the JWT through an interceptor that only covers ApiService calls.
    // So this renders a broken image, not a document.
    //
    // The way off the public bucket is a presigned GET URL minted by the backend and
    // put in the permit payload: time-limited, needs no auth from the browser, and
    // works in a plain <img>. Until then `path` is what actually works.
    if (doc.key) return `${environment.apiUrl}/api/upload/open/${encodeURIComponent(doc.key)}`;

    return null;
}

/**
 * Whether to show the document itself rather than a link to it.
 *
 * Reviewers are approving a fishing permit by looking at the boat photo, so making
 * them open a tab for it is the wrong way round. Decided on the stored MIME type
 * rather than the filename - the extension is whatever the applicant's phone called
 * it, the type is what was actually uploaded. Anything that is not an image (a PDF
 * police check, most often) stays a link, because a PDF in an <img> renders nothing.
 */
export function isViewableImage(doc?: UploadedDocument | null): boolean {
    return !!doc && !!doc.type && doc.type.startsWith('image/');
}
